import React, { Component } from 'react';
import { connect } from 'react-redux';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Pip from '@material-ui/icons/PictureInPictureAlt';

import loading from '../../assets/icons/loading.gif';
import { togglePip } from '../../lib/actions/room';
import { RtcDevices } from '../../lib/api/rtcDevices';
import { getLogger } from '../../lib/logger';
import { isLessonLayout, isWebinarLayout } from '../../lib/reduxSelectors/room';
import { getWsUserId } from '../../lib/reduxSelectors/websocket';
import { getInitials } from '../../lib/utils/displayName';
import PiP from '../../pip';
import prepareWebRtcProvider from '../../rtc';

import style from './style';


function getMutedProp(props) {
  if (props.muted != null) {
    return props.muted;
  }

  if (props.isInDifferentAudioConf) {
    return true;
  }

  if (!props.user) {
    return true;
  }

  if (props.user.endsWith('_screen') && !props.user.startsWith(props.myUserId)) {
    return false;
  }

  return true;
}

class VideoElement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      blur: props.shouldBlur,
      timerId: null
    };
  }

  onPipDisabled = () => {
    this.props.dispatch(togglePip(null));
  }

  onPipEnabled = () => {
    this.props.dispatch(togglePip(this.props.user));
  }

  onPause = () => {
    if (this.video) {
      this.video.play();
    }
  }

  componentDidMount() {
    this.pip = new PiP(this.video);
    if (this.props.src) {
      this.video.srcObject = this.props.src;
    }
    this.video.muted = getMutedProp(this.props);
    this.pip.addListeners(this.onPipEnabled, this.onPipDisabled);
    this.video.addEventListener('pause', this.onPause);
  }

  componentWillUnmount() {
    if (this.props.pipEnabled && this.props.user === this.props.pipEnabled) {
      this.props.dispatch(togglePip(null));
      this.pip.set(null);
      this.pip.removeListeners(this.onPipEnabled, this.onPipDisabled);
    }
    if (this.video) {
      this.video.src = '';
      this.video.srcObject = null;
      this.video.removeEventListener('pause', this.onPause);
    }
    if (this.state.timerId) {
      clearTimeout(this.state.timerId);
      this.setState({ timerId: null });
    }
  }

  componentDidUpdate(prevProps) {
    if (!!this.props.src && this.props.src !== prevProps.src) {
      this.video.srcObject = this.props.src;
    }
    if (getMutedProp(this.props) !== this.video.muted) {
      this.video.muted = getMutedProp(this.props);
    }
    if (this.props.handleAudioOutputChange) {
      this.setOutputDevice(prevProps.sink, this.props.sink);
    }
    if (this.props.user === this.props.pipEnabled && this.props.pipEnabled !== prevProps.pipEnabled) {
      this.pip.set(this.props.pipEnabled);
    }
    if (this.props.user === prevProps.pipEnabled && this.props.pipEnabled !== prevProps.pipEnabled) {
      this.pip.set(null);
    }
    if (this.props.shouldBlur !== prevProps.shouldBlur) {
      if (this.state.timerId) {
        clearTimeout(this.state.timerId);
        this.setState({ timerId: null });
      } else {
        const timer = setTimeout(() => {
          this.setState({
            blur: this.props.shouldBlur,
            timerId: null,
          });
        }, 100);
        this.setState({ timerId: timer });
      }
    }
  }

  setOutputDevice(oldDevice, newDevice) {
    const logger = getLogger('VideoElement Set Output');
    const webrtc = prepareWebRtcProvider();
    const rtc = new RtcDevices(webrtc, logger);

    if (newDevice && newDevice !== oldDevice) {
      rtc.setAudioOutputOn(this.video, newDevice).then(() => {
        logger.debug("Changed audio output to ", newDevice);
      }).catch((e) => logger.error(e));
    }
  }

  doNothing() {
    // This is ugly, but to allow hovering on ios, the container element must
    // be clickable, so for now just add an empty click callback
    // cfr. https://developer.apple.com/library/content/documentation/AppleApplications/Reference/SafariWebContent/HandlingEvents/HandlingEvents.html
  }

  onDoubleClick = () => {
    let user = this.props.user;
    if (user) {
      let stream_type = 'stream';
      if (user.endsWith('_screen')) {
        stream_type = 'screen';
      }
      user = user.replace(/_screen$/, '');

      if (this.props.onDoubleClick) {
        this.props.onDoubleClick(user, stream_type);
      }
    }
  }

  render() {
    const classes = this.props.classes;
    return (
      <div
        className={classNames({
          [classes.container]: true,
          [classes.overflowHidden]: this.state.blur,
          [classes.rounded]: this.props.rounded,
        })}
        onDoubleClick={this.onDoubleClick}>
        <div className={classes.fullHeight}>
          <video className={classNames({
            [classes.video]: true,
            [classes.mirrored]: this.props.mirrored,
            [classes.rounded]: this.props.rounded,
            [classes.muted]: this.props.isVideoMuted,
            [classes.fullHeight]: this.props.fullHeight,
            [classes.fit]: this.props.fit,
            [classes.talkingIndicator]: this.props.isTalking && !this.props.circled,
            [classes.circled]: this.props.circled,
            [classes.videoBlurred]: this.state.blur,
          })}
          autoPlay
          playsInline
          poster={!this.props.isVideoMuted ? loading : null}
          ref={(ref) => {
            this.video = ref;
            if (this.props.videoRef)
              this.props.videoRef.current = ref;
          }} onClick={this.doNothing}
          />
        </div>
        {this.props.isVideoMuted && this.props.addVideoMutedIconOverlay &&
          <div className={classNames({
            [classes.videoMutedOverlayContainer]: true,
            [classes.rounded]: this.props.rounded,
            [classes.fit]: this.props.fit,
          })}>
            <Typography className={classes.videoMutedInitials}>
              {getInitials(this.props.display)}
            </Typography>
          </div>
        }
        {this.props.pipEnabled && this.props.pipEnabled === this.props.user &&
          <div className={classes.videoPipOverlayContainer}>
            <Pip style={{ height: '50%', width: '50%' }}
              className={classes.videoPipOverlay} />
          </div>
        }
      </div>
    );
  }
}


VideoElement.propTypes = {
  src: PropTypes.object.isRequired,
  mirrored: PropTypes.bool,
  rounded: PropTypes.bool,
  muted: PropTypes.bool,
  handleAudioOutputChange: PropTypes.bool,
  sink: PropTypes.object,
  fullHeight: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  isVideoMuted: PropTypes.bool,
  isTalking: PropTypes.bool,
  user: PropTypes.string,
  onDoubleClick: PropTypes.func,
  addVideoMutedIconOverlay: PropTypes.bool,
  pipEnabled: PropTypes.string,
  onPipEnabled: PropTypes.func,
  onPipDisabled: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  fit: PropTypes.bool,
  display: PropTypes.string,
  circled: PropTypes.bool,
  videoRef: PropTypes.oneOfType([
    PropTypes.func, // for legacy refs
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLVideoElement) })
  ]),
  myUserId: PropTypes.string,
  shouldBlur: PropTypes.bool,
};


VideoElement.defaultProps = {
  rounded: false,
  fullHeight: true,
  handleAudioOutputChange: false,
  addVideoMutedIconOverlay: false,
  pipEnabled: null,
  fit: false,
  circled: false,
};


function getVideoMutedProperty(state, user) {
  if (!user) {
    return false;
  }
  let prop = 'isVideoMuted';
  if (user.endsWith('_screen')) {
    prop = 'isScreenMuted';
  }
  user = user.replace(/_screen$/, '');
  return (state.room.roster[user] || { prop: false })[prop];
}


function detectTalking(state, user) {
  if (!user) {
    return false;
  }
  return (state.room.roster[user] || { is_talking: false }).is_talking;
}


function getDisplayName(state, user) {
  if (!user) {
    return '';
  }
  return (state.room.roster[user] || { display: '' }).display;
}


function mapStateToProps(state, props) {
  const user = getWsUserId(state.websocket);
  const videoOwner =
    props.user && props.user.endsWith('_screen') ? props.user.replace(/_screen/, "") : props.user;
  let shouldBlur = false;

  if (user && videoOwner) {
    const amInPrivateConf = (state.room.roster[user] || { privateAudioConf: null }).privateAudioConf;
    const isInPrivateConf = (state.room.roster[videoOwner] || { privateAudioConf: null }).privateAudioConf;
    const isInDifferentAudioConf = amInPrivateConf !== isInPrivateConf;
    shouldBlur = isInDifferentAudioConf && !isLessonLayout(state) && !isWebinarLayout(state);
  }
  return {
    isVideoMuted: getVideoMutedProperty(state, props.user),
    sink: state.settings.audioOutDevice,
    isTalking: detectTalking(state, props.user),
    pipEnabled: state.room.pipEnabled,
    display: getDisplayName(state, props.user),
    myUserId: getWsUserId(state.websocket),
    shouldBlur: shouldBlur,
  };
}

export default withStyles(style)(connect(mapStateToProps, null, null, { forwardRef: true })(
  React.forwardRef((props, ref) => (
    <VideoElement videoRef={ref} {...props} />
  ))
));
